'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * The three "How it works" illustrations.
 *
 * Steps 1 and 3 are single pre-composed final photos (one per breakpoint, from Figma) —
 * see StepOneArt / StepThreeArt below. Step 2 is still a layered composition rebuilt on a
 * fixed design canvas, with every offset/size expressed in `cqw` (container-query width
 * units) so it scales proportionally with the card at any width.
 */

const BASE = '/images/comix/homepage/v2';
/** Matches the `dt:` breakpoint (800px) — same constant HeroVideo.tsx uses for its
 * <source media>, so the mobile/desktop art sources switch at the same width. */
const MOBILE_QUERY = '(max-width: 799px)';

/** px on the design canvas -> a width-proportional unit. */
const u = (px: number, canvas: number) => `${((px / canvas) * 100).toFixed(4)}cqw`;

type CanvasProps = {
  width: number;
  height: number;
  className?: string;
  onClick?: () => void;
  onHoverChange?: (hovered: boolean) => void;
  /** Set when onClick toggles playback — wires up the keyboard path and the button semantics. */
  controlLabel?: string;
  pressed?: boolean;
  children: React.ReactNode;
};

function Canvas({ width, height, className = '', onClick, onHoverChange, controlLabel, pressed, children }: CanvasProps) {
  return (
    <div
      className={`relative w-full ${className}`}
      style={{
        containerType: 'inline-size',
        aspectRatio: `${width} / ${height}`,
        isolation: 'isolate',
      }}
      onClick={onClick}
      onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
      onFocus={onHoverChange ? () => onHoverChange(true) : undefined}
      onBlur={onHoverChange ? () => onHoverChange(false) : undefined}
      onKeyDown={
        onClick && controlLabel
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
            }
          : undefined
      }
      role={onClick && controlLabel ? 'button' : undefined}
      tabIndex={onClick && controlLabel ? 0 : undefined}
      aria-label={controlLabel}
      aria-pressed={controlLabel ? pressed : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Play/pause badge shown on hover — the same affordance HeroCoverReel draws over the hero cover
 * (black at 50%, 140ms fade, pause while running / play while stopped). That one lives in SVG user
 * space; this one is a positioned DOM node on the cqw canvas, so the markup can't be shared — keep
 * the two visually in step by hand.
 */
function TransportBadge({
  visible, paused, left, top, size,
}: { visible: boolean; paused: boolean; left: string; top: string; size: string }) {
  return (
    <div
      className="pointer-events-none absolute flex items-center justify-center rounded-full"
      style={{
        left, top, width: size, height: size, zIndex: 4,
        backgroundColor: 'rgba(0,0,0,0.5)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 240ms ease',
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" className="size-1/2" fill="white">
        {paused ? <path d="M8 5 L19 12 L8 19 Z" /> : <><rect x="6" y="4.5" width="4" height="15" rx="1.2" /><rect x="14" y="4.5" width="4" height="15" rx="1.2" /></>}
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------------- step 1 */
/* A single pre-composed photo (transparent background, so the card's own tint shows
 * through) per breakpoint — replaces the old hand-assembled 8-asset composition.
 * <picture>/<source media> (not two <img> tags toggled by CSS) — a plain hidden <img> is
 * still fetched by the browser regardless of display:none, so two <img>s downloaded both
 * variants on every load. <picture> guarantees only the matching source is ever fetched. */
export function StepOneArt() {
  return (
    <picture>
      <source media={MOBILE_QUERY} srcSet={`${BASE}/step1-mobile.avif`} />
      <img
        src={`${BASE}/step1-desktop.avif`}
        alt="Photos of a dog, a girl, and a boy, with a dashed slot and a hand tapping it to add one more"
        className="w-full aspect-[1080/538] dt:aspect-[990/870]"
      />
    </picture>
  );
}

/* ---------------------------------------------------------------- step 2 */
/* Figma 303:3177 — three comic covers fanned out in a center/left/right stack, now
 * driving a true rotating carousel over 6 sample covers: each cover travels a full
 * circuit — entering from the left, advancing to center, advancing to the back-right,
 * then exiting — while the next cover enters behind it. Every 1.3s the whole ring
 * advances one step (400ms spring animation, then a 900ms hold), so all 6 covers take
 * their turn through every stage. */
const S2 = 361;
const s2 = (px: number) => u(px, S2);

const COVER_SHADOW = `${s2(-4.504)} ${s2(4.504)} ${s2(4.504)} rgba(0,0,0,0.11), ${s2(-9.009)} ${s2(9.009)} ${s2(11.261)} rgba(0,0,0,0.1)`;
const COVER_SHEEN =
  'linear-gradient(90deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.2) 100%), linear-gradient(90deg, rgba(255,255,255,0.2) 0.11872%, rgba(255,255,255,0) 7.3252%)';
const CENTER_SHEEN = 'linear-gradient(90deg, rgba(255,255,255,0.2) 0.119%, rgba(255,255,255,0) 7.325%)';

const COVER_IMAGES = [
  `${BASE}/step2-cover-beagles-blazing-run.avif`,
  `${BASE}/step2-cover-dream-loot-disaster.avif`,
  `${BASE}/step2-cover-mask-off-pants-near.avif`,
  `${BASE}/step2-cover-wet-block-uprising.avif`,
  `${BASE}/step2-cover-rock-n-roll-time-jam.avif`,
  `${BASE}/step2-cover-wine-teeth-and-trouble.avif`,
];

type Stage = { centerX: number; centerY: number; width: number; height: number; rotate: number; opacity: number; sheen: string; z: number };

/* left/right sit equidistant from center (95.0235) and share centerY (143.138) — Figma's
 * raw values had them off-balance both vertically (~17 apart) and horizontally (84.31
 * from center on the left vs 105.74 on the right). */
const STAGE_LEFT: Stage = { centerX: 85.8075, centerY: 143.138, width: 139.335, height: 180.421, rotate: -7, opacity: 1, sheen: COVER_SHEEN, z: 2 };
const STAGE_CENTER: Stage = { centerX: 180.831, centerY: 135.496, width: 160.137, height: 207.356, rotate: 0, opacity: 1, sheen: CENTER_SHEEN, z: 3 };
const STAGE_RIGHT: Stage = { centerX: 275.8545, centerY: 143.138, width: 139.335, height: 180.421, rotate: 7, opacity: 1, sheen: COVER_SHEEN, z: 1 };
/* Hidden staging positions, just past the visible left/right slots — invisible (opacity
 * 0), but positioned so the fade-in/out has somewhere to animate from/to instead of
 * popping. EXIT continues the right cover's path further back; PRE_ENTRY sits just
 * outside the left slot, ready to come up. */
const STAGE_EXIT: Stage = { centerX: 315, centerY: 158, width: 139.335, height: 180.421, rotate: 14, opacity: 0, sheen: COVER_SHEEN, z: 0 };
const STAGE_PRE_ENTRY: Stage = { centerX: 46, centerY: 158, width: 139.335, height: 180.421, rotate: -14, opacity: 0, sheen: COVER_SHEEN, z: 0 };

/* The full ring a single cover cycles through, one stage per tick: left -> center ->
 * right (back) -> exit -> (hidden, waiting) -> (hidden, waiting) -> left again. Always the
 * same ring/modulus for both the initial 3-cover state and the full 6-cover state — using
 * a *different* ring (and modulus) for the 3-cover state used to make cover IDs 1 and 2
 * land on completely different offsets the instant the animation started, so the photos
 * visibly swapped out for different ones instead of continuing smoothly. Starting
 * headIndex at 2 (not 0) puts cover IDs 0/1/2 exactly on RIGHT/CENTER/LEFT from the first
 * frame, and cover IDs 3/4/5 land on the hidden EXIT/PRE_ENTRY stages — so once they're
 * added to the map, they fade in from those hidden stages instead of popping into view. */
const RING: Stage[] = [STAGE_LEFT, STAGE_CENTER, STAGE_RIGHT, STAGE_EXIT, STAGE_PRE_ENTRY, STAGE_PRE_ENTRY];
const INITIAL_HEAD_INDEX = 2;

const SWITCH_DURATION = 0.4; // seconds — spring animation to the next stage
const SWITCH_DELAY = 0.9; // seconds — hold before the next advance
const CYCLE_MS = (SWITCH_DURATION + SWITCH_DELAY) * 1000;

/** Badge diameter on the design canvas — matched to the hero's ratio against its centre cover. */
const S2_BADGE = 40;

export function StepTwoArt() {
  // Drives mobile-only native lazy-loading on the cover <img> tags below (desktop stays eager).
  const isMobile = useIsMobile();
  const [headIndex, setHeadIndex] = useState(INITIAL_HEAD_INDEX);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Mobile only: the badge appears on tap. While the carousel is RUNNING it self-hides 2s
  // later so it stops covering the covers; while it's PAUSED it stays put (you need to see
  // the ▶ to know a tap will resume it). Desktop is unchanged — hover drives it.
  const [tappedAt, setTappedAt] = useState(0);
  // Defers the last 3 covers until the rest of the page (window "load") has finished, so
  // this carousel doesn't compete with everything else for bandwidth on first paint.
  const [assetsReady, setAssetsReady] = useState(false);

  useEffect(() => {
    if (!isMobile || tappedAt === 0 || paused) return;
    const id = setTimeout(() => setTappedAt(0), 2000);
    return () => clearTimeout(id);
  }, [isMobile, tappedAt, paused]);

  useEffect(() => {
    if (document.readyState === 'complete') {
      setAssetsReady(true);
      return;
    }
    const handleLoad = () => setAssetsReady(true);
    window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, []);

  const images = assetsReady ? COVER_IMAGES : COVER_IMAGES.slice(0, 3);

  useEffect(() => {
    // Before assetsReady, the first 3 covers sit still (no animation) — the interval (and
    // the rotating carousel) only starts once all 6 are in.
    if (paused || !assetsReady) return;
    const id = setInterval(() => setHeadIndex((h) => (h + 1) % RING.length), CYCLE_MS);
    return () => clearInterval(id);
  }, [paused, assetsReady]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleClick = React.useCallback(() => {
    setPaused((p) => !p);
    setTappedAt(Date.now()); // re-arm the mobile auto-hide on every tap
  }, []);
  const handleHoverChange = React.useCallback((isHovered: boolean) => setHovered(isHovered), []);
  const controlLabel = paused ? 'Resume the animation' : 'Stop the animation';

  return (
    <Canvas
      width={361}
      height={231}
      className="step2-canvas cursor-pointer overflow-hidden"
      onClick={handleClick}
      onHoverChange={handleHoverChange}
      controlLabel={controlLabel}
      pressed={paused}
    >
      {images.map((src, coverId) => {
        const stage = RING[(headIndex - coverId + RING.length * 1000) % RING.length];
        // `animate`-only values aren't rendered into the server HTML's style attribute —
        // Framer Motion applies them via a client-side effect after mount, so without an
        // explicit `initial` there's a flash of unpositioned (0-size) content before
        // hydration. Setting `initial` to the same first-frame values renders them
        // synchronously up front; `animate` then takes over for every stage change after.
        const stageStyle = {
          left: s2(stage.centerX - stage.width / 2),
          top: s2(stage.centerY - stage.height / 2),
          width: s2(stage.width),
          height: s2(stage.height),
          rotate: stage.rotate,
          opacity: stage.opacity,
        };
        return (
          <motion.div
            key={src}
            className="absolute"
            style={{ boxShadow: COVER_SHADOW, zIndex: stage.z }}
            initial={stageStyle}
            animate={stageStyle}
            transition={{ type: 'spring', duration: SWITCH_DURATION, bounce: 0.22 }}
          >
            <img
              src={src}
              alt=""
              aria-hidden="true"
              loading={isMobile ? 'lazy' : undefined}
              className="absolute inset-0 size-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundImage: stage.sheen }} />
          </motion.div>
        );
      })}

      {/* Centred on the CENTER cover, above it (that stage is z:3). Only once the carousel is
          actually running — before assetsReady the covers sit still, so there is nothing to stop. */}
      <TransportBadge
        visible={assetsReady && (isMobile ? paused || tappedAt !== 0 : hovered)}
        paused={paused}
        left={s2(STAGE_CENTER.centerX - S2_BADGE / 2)}
        top={s2(STAGE_CENTER.centerY - S2_BADGE / 2)}
        size={s2(S2_BADGE)}
      />
    </Canvas>
  );
}

/* ---------------------------------------------------------------- step 3 */
/* A single finished product photo (own opaque background baked in) per breakpoint —
 * replaces the old book-cutout + colored-panel + darken-blend composition. Full width,
 * natural aspect ratio on both breakpoints — no crop. Desktop's photo is near-square, so
 * at 100% width it's naturally taller than the other two cards' art; .hp-step-art uses
 * min-height (not a hard height) so the card grows to fit instead of the photo cropping. */
export function StepThreeArt() {
  return (
    <picture>
      <source media={MOBILE_QUERY} srcSet={`${BASE}/step3-mobile.avif`} />
      <img
        src={`${BASE}/step3-desktop.avif`}
        alt="A finished, printed 'Legendary Chaos' comic book stack"
        className="w-full aspect-[1080/1008] dt:aspect-[990/990]"
      />
    </picture>
  );
}
